// src/pages/AffectationsList.js - AVEC GESTION DES ERREURS D'ABONNEMENT POUR SUPPRESSION
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const AffectationsList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const siteIdParam = queryParams.get('site_id');
  const agentIdParam = queryParams.get('agent_id');
  
  const [affectations, setAffectations] = useState([]);
  const [statistiques, setStatistiques] = useState({
    total_sites: 0,
    total_agents: 0,
    total_affectations: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  const [filters, setFilters] = useState({
    id_site: siteIdParam || "",
    id_agent: agentIdParam || ""
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedAffectation, setSelectedAffectation] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [hoveredDelete, setHoveredDelete] = useState(null);
  const [filterSites, setFilterSites] = useState([]);
  const [filterAgents, setFilterAgents] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);

  // Palette de couleurs SecureControl
  const COLORS = {
    primary: '#0B3C5D',
    secondary: '#1E88C8',
    accent: '#4FC3F7',
    darkText: '#2E2E2E',
    lightBg: '#F5F7FA',
    white: '#FFFFFF',
    danger: '#f44336',
    success: '#4caf50',
    warning: '#ff9800',
    info: '#2196f3'
  };

  // Récupérer les infos utilisateur
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        const normalizedData = {
          ...userData,
          profile: userData.profile || userData.poste || 0,
          societe_id: userData.societe_id || 0
        };
        setUserInfo(normalizedData);
      } catch (error) {
        navigate("/login");
      }
    } else {
      navigate("/login");
    }
  }, [navigate]);

  // Charger les données pour les filtres - CORRIGÉ
  const fetchFilterData = useCallback(async () => {
    if (!userInfo) return;
    
    const token = localStorage.getItem("access_token");
    if (!token) return;
    
    setLoadingFilters(true);
    
    try {
      // Charger les sites - ESSAYER PLUSIEURS ENDPOINTS
      const sitesEndpoints = [
        `${API_BASE_URL}/sites/list.php?limit=1000`,
        `${API_BASE_URL}/sites.php?action=list&limit=1000`,
        `${API_BASE_URL}/sites.php`
      ];
      
      let sitesData = [];
      for (const endpoint of sitesEndpoints) {
        try {
          const sitesResponse = await axios.get(endpoint, {
            headers: { 
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          });
          
          if (sitesResponse.data?.success) {
            sitesData = sitesResponse.data.data || sitesResponse.data.sites || [];
            break;
          }
        } catch (err) {
          console.warn(`Erreur endpoint ${endpoint}:`, err.message);
          continue;
        }
      }
      
      setFilterSites(sitesData);
      
      // Charger les agents - ESSAYER PLUSIEURS ENDPOINTS
      const agentsEndpoints = [
        `${API_BASE_URL}/agents/list.php?limit=1000`,
        `${API_BASE_URL}/agents.php?action=list&limit=1000`,
        `${API_BASE_URL}/agents.php`,
        `${API_BASE_URL}/personnel/list.php?limit=1000`
      ];
      
      let agentsData = [];
      for (const endpoint of agentsEndpoints) {
        try {
          const agentsResponse = await axios.get(endpoint, {
            headers: { 
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          });
          
          if (agentsResponse.data?.success) {
            agentsData = agentsResponse.data.data || agentsResponse.data.agents || [];
            break;
          }
        } catch (err) {
          console.warn(`Erreur endpoint ${endpoint}:`, err.message);
          continue;
        }
      }
      
      // Si toujours vide, essayer une requête plus simple
      if (agentsData.length === 0) {
        try {
          const simpleResponse = await axios.get(`${API_BASE_URL}/agents.php`, {
            headers: { 
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          });
          
          if (simpleResponse.data) {
            if (Array.isArray(simpleResponse.data)) {
              agentsData = simpleResponse.data;
            } else if (simpleResponse.data.agents) {
              agentsData = simpleResponse.data.agents;
            } else if (simpleResponse.data.data) {
              agentsData = simpleResponse.data.data;
            }
          }
        } catch (err) {
          console.error("Erreur chargement agents simple:", err);
        }
      }
      
      setFilterAgents(agentsData);
      
    } catch (err) {
      console.error("Erreur chargement filtres:", err);
      setError(`Erreur chargement filtres: ${err.message}`);
    } finally {
      setLoadingFilters(false);
    }
  }, [userInfo]);

  // Charger les affectations - CORRIGÉ
  const fetchAffectations = useCallback(async () => {
    if (!userInfo) return;
    
    if (String(userInfo.profile).trim() === "1") {
      setError("Permission refusée. L'administrateur système ne peut pas accéder aux affectations.");
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError("");
    
    const token = localStorage.getItem("access_token");
    if (!token) {
      setError("Session expirée");
      setTimeout(() => navigate("/login"), 2000);
      return;
    }
    
    try {
      let url = `${API_BASE_URL}/affectations/list.php`;
      const queryParams = [];
      
      if (filters.id_site) {
        queryParams.push(`id_site=${filters.id_site}`);
      }
      if (filters.id_agent) {
        queryParams.push(`id_agent=${filters.id_agent}`);
      }
      
      if (queryParams.length > 0) {
        url += `?${queryParams.join('&')}`;
      }
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        timeout: 10000
      });
      
      if (response.data?.success) {
        const affectationsData = response.data.data || response.data.affectations || [];
        setAffectations(affectationsData);
        
        if (response.data.statistiques) {
          setStatistiques(response.data.statistiques);
        } else {
          const uniqueSites = [...new Set(affectationsData.map(a => a.id_site))];
          const uniqueAgents = [...new Set(affectationsData.map(a => a.id_agent))];
          
          setStatistiques({
            total_sites: uniqueSites.length,
            total_agents: uniqueAgents.length,
            total_affectations: affectationsData.length
          });
        }
      } else {
        try {
          const altResponse = await axios.get(`${API_BASE_URL}/affectations.php`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            }
          });
          
          if (altResponse.data?.success) {
            const affectationsData = altResponse.data.data || altResponse.data.affectations || [];
            setAffectations(affectationsData);
            
            const uniqueSites = [...new Set(affectationsData.map(a => a.id_site))];
            const uniqueAgents = [...new Set(affectationsData.map(a => a.id_agent))];
            
            setStatistiques({
              total_sites: uniqueSites.length,
              total_agents: uniqueAgents.length,
              total_affectations: affectationsData.length
            });
          } else {
            setError(response.data?.message || "Erreur de chargement des affectations");
          }
        } catch (altErr) {
          setError(response.data?.message || "Erreur de chargement des affectations");
        }
      }
    } catch (err) {
      console.error("Erreur:", err);
      
      if (err.response?.status === 401) {
        setError("Session expirée. Veuillez vous reconnecter.");
        localStorage.clear();
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 403) {
        setError("Accès interdit. Vous n'avez pas les permissions nécessaires.");
      } else if (err.code === 'ECONNABORTED' || !err.response) {
        setError("Serveur indisponible ou timeout. Vérifiez votre connexion.");
      } else {
        setError(`Erreur ${err.response.status}: ${err.response.data?.message || "Erreur technique"}`);
      }
    } finally {
      setLoading(false);
    }
  }, [userInfo, navigate, filters]);

  useEffect(() => {
    if (userInfo) {
      fetchFilterData();
      fetchAffectations();
    }
  }, [userInfo, fetchFilterData, fetchAffectations]);

  // Gestion des filtres
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyFilters = () => {
    setShowFilters(false);
    fetchAffectations();
  };

  const resetFilters = () => {
    setFilters({
      id_site: "",
      id_agent: ""
    });
    setShowFilters(false);
    fetchAffectations();
  };

  // Supprimer une affectation
  const handleDeleteAffectation = (affectation) => {
    setSelectedAffectation(affectation);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedAffectation) return;
    
    setActionLoading(true);
    
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.post(
        `${API_BASE_URL}/affectations/delete.php`,
        {
          id_site: selectedAffectation.id_site,
          id_agent: selectedAffectation.id_agent
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      
      if (response.data?.success) {
        setAffectations(prev => prev.filter(a => 
          !(a.id_site === selectedAffectation.id_site && a.id_agent === selectedAffectation.id_agent)
        ));
        
        setStatistiques(prev => ({
          ...prev,
          total_affectations: prev.total_affectations - 1
        }));
        
        setShowDeleteModal(false);
        setSelectedAffectation(null);
      } else {
        setError(response.data?.message || "Erreur lors de la suppression");
      }
    } catch (err) {
      console.error("Erreur:", err);
      
      // 🔍 Vérifier si c'est une erreur d'abonnement
      if (err.response?.data?.code === 'ABONNEMENT_REQUIS') {
        // Message avec bouton de souscription
        setError(
          <div>
            <div style={{ whiteSpace: 'pre-line', marginBottom: '15px' }}>
              {err.response.data.message}
            </div>
            <div className="d-flex gap-2 justify-content-center">
              <a 
                href="/abonnements/create"
                className="btn fw-semibold"
                style={{
                  backgroundColor: '#4caf50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 20px',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#4caf50'}
              >
                <i className="bi bi-box-arrow-up-right"></i>
                Souscrire à un abonnement
              </a>
              <button
                className="btn fw-semibold"
                onClick={() => setError("")}
                style={{
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 20px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#f44336'}
              >
                Fermer
              </button>
            </div>
          </div>
        );
        setShowDeleteModal(false);
        setSelectedAffectation(null);
      } else {
        setError(err.response?.data?.message || "Erreur technique");
      }
    } finally {
      setActionLoading(false);
    }
  };

  // Permissions
  const isGestionnaire = userInfo && String(userInfo.profile).trim() === "3";
  const isControleur = userInfo && String(userInfo.profile).trim() === "2";
  
  const isDeleted = (affectation) => {
    const siteStatus = affectation.site?.statut || affectation.site?.statut_site || 0;
    const agentStatus = affectation.agent?.statut || affectation.agent?.statut_agent || 0;
    return siteStatus === 0 || agentStatus === 0;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString || dateString === "0000-00-00" || dateString === "0000-00-00 00:00:00") {
      return "-";
    }
    try {
      return new Date(dateString).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Formater le nom de l'agent
  const formatAgentName = (agent) => {
    if (!agent) return "Inconnu";
    if (agent.prenom && agent.nom) {
      return `${agent.prenom} ${agent.nom}`;
    }
    if (agent.nom_agent) {
      return agent.nom_agent;
    }
    return `Agent ${agent.id_agent}`;
  };

  // Formater le nom du site
  const formatSiteName = (site) => {
    if (!site) return "Inconnu";
    if (site.nom) return site.nom;
    if (site.nom_site) return site.nom_site;
    return `Site ${site.id_site}`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Gestion des affectations</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard" style={{ color: COLORS.secondary }}>Tableau de bord</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Chargement...</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                <span className="visually-hidden">Chargement...</span>
              </div>
              <p className="mt-2" style={{ color: COLORS.darkText }}>Chargement des affectations...</p>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  if (error && !loading) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Gestion des affectations</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard" style={{ color: COLORS.secondary }}>Tableau de bord</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.danger }}>Erreur</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body">
              <div className="alert alert-danger alert-dismissible fade show" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.danger}`,
                whiteSpace: "pre-line"
              }}>
                <div className="d-flex align-items-start">
                  <i className="bi bi-exclamation-triangle me-2 mt-1"></i>
                  <div className="flex-grow-1">{error}</div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setError("")}
                  ></button>
                </div>
              </div>
              <div className="text-center mt-3">
                <button 
                  className="btn fw-semibold"
                  onClick={() => navigate("/dashboard")}
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '10px 24px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.primary;
                    e.target.style.color = COLORS.white;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = COLORS.primary;
                  }}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Retour au tableau de bord
                </button>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  if (isControleur) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Gestion des affectations</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard" style={{ color: COLORS.secondary }}>Tableau de bord</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Affectations</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="mb-4">
                <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                  width: '80px',
                  height: '80px',
                  backgroundColor: '#F5F7FA',
                  color: '#B0BEC5'
                }}>
                  <i className="bi bi-people fs-1"></i>
                </div>
              </div>
              <h5 className="mb-3" style={{ color: COLORS.primary, fontWeight: '600' }}>Mode consultation</h5>
              <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                En tant que contrôleur, vous ne pouvez voir que vos affectations personnelles.
                <br />
                Contactez votre gestionnaire pour plus d'informations.
              </p>
              <button
                className="btn fw-semibold mt-4"
                onClick={() => navigate("/dashboard")}
                style={{
                  color: COLORS.primary,
                  borderColor: COLORS.primary,
                  backgroundColor: 'transparent',
                  borderRadius: '8px',
                  padding: '10px 24px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = COLORS.primary;
                  e.target.style.color = COLORS.white;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = COLORS.primary;
                }}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Retour au tableau de bord
              </button>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Gestion des affectations</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard" style={{ color: COLORS.secondary }}>Tableau de bord</Link>
            </li>
            <li className="breadcrumb-item">Gestion</li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Affectations</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h5 className="card-title mb-1" style={{ 
                  color: COLORS.primary,
                  borderBottom: `2px solid ${COLORS.lightBg}`,
                  paddingBottom: '16px',
                  width: '100%'
                }}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-3" style={{ 
                      width: '36px',
                      height: '36px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-people"></i>
                    </div>
                    Liste des affectations
                    <span className="badge ms-3" style={{ 
                      backgroundColor: COLORS.secondary,
                      color: COLORS.white,
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '0.8rem'
                    }}>
                      {affectations.length} trouvée(s)
                    </span>
                  </div>
                </h5>
                <p className="text-muted mb-0" style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                  <i className="bi bi-info-circle me-1"></i>
                  Gérez les affectations des agents aux sites de surveillance
                  {loadingFilters && (
                    <span className="ms-2" style={{ color: COLORS.secondary }}>
                      <small>Chargement des filtres...</small>
                    </span>
                  )}
                </p>
              </div>
              
              <div className="d-flex gap-2">
                <button
                  className="btn fw-semibold"
                  onClick={() => setShowFilters(!showFilters)}
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '10px 20px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#E3F2FD';
                    e.target.style.borderColor = COLORS.secondary;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.borderColor = COLORS.primary;
                  }}
                >
                  <i className="bi bi-funnel me-2"></i>
                  Filtres
                  {filters.id_site || filters.id_agent ? (
                    <span className="badge fw-semibold ms-1" style={{ 
                      backgroundColor: COLORS.danger,
                      color: 'white',
                      fontSize: '0.7rem'
                    }}>!</span>
                  ) : null}
                </button>
                
                {isGestionnaire && (
                  <button
                    className="btn fw-semibold"
                    onClick={() => navigate("/affectations/create")}
                    style={{
                      color: COLORS.white,
                      backgroundColor: COLORS.primary,
                      borderColor: COLORS.primary,
                      borderRadius: '8px',
                      padding: '10px 24px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = COLORS.secondary;
                      e.target.style.borderColor = COLORS.secondary;
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 12px rgba(11, 60, 93, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = COLORS.primary;
                      e.target.style.borderColor = COLORS.primary;
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <i className="bi bi-plus-lg me-2"></i>
                    Nouvelle affectation
                  </button>
                )}
              </div>
            </div>

            {/* Filtres - AMÉLIORÉ */}
            {showFilters && (
              <div className="card mb-4" style={{ 
                backgroundColor: '#E3F2FD',
                border: 'none',
                borderRadius: '8px'
              }}>
                <div className="card-body py-3">
                  <h6 className="card-title mb-3" style={{ 
                    color: COLORS.primary,
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                      width: '28px',
                      height: '28px',
                      backgroundColor: COLORS.white,
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-funnel"></i>
                    </div>
                    Filtres de recherche
                    {loadingFilters && (
                      <span className="ms-2 spinner-border spinner-border-sm" role="status" style={{ 
                        color: COLORS.primary,
                        width: '1rem',
                        height: '1rem'
                      }}></span>
                    )}
                  </h6>
                  
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="input-group">
                        <span className="input-group-text" style={{ 
                          backgroundColor: COLORS.white,
                          borderColor: '#e0e0e0',
                          color: COLORS.secondary
                        }}>
                          <i className="bi bi-building"></i>
                        </span>
                        <select
                          className="form-select"
                          name="id_site"
                          value={filters.id_site}
                          onChange={handleFilterChange}
                          style={{
                            borderColor: '#e0e0e0',
                            color: COLORS.darkText
                          }}
                          disabled={loadingFilters}
                        >
                          <option value="">Tous les sites</option>
                          {filterSites.length > 0 ? (
                            filterSites.map(site => (
                              <option key={site.id_site || site.id} value={site.id_site || site.id}>
                                {site.nom_site || site.nom} 
                              </option>
                            ))
                          ) : (
                            <option value="" disabled>Aucun site disponible</option>
                          )}
                        </select>
                      </div>
                      <div className="form-text" style={{ fontSize: '0.75rem' }}>
                        {filterSites.length} site(s) disponible(s)
                      </div>
                    </div>
                    
                    <div className="col-md-6">
                      <div className="input-group">
                        <span className="input-group-text" style={{ 
                          backgroundColor: COLORS.white,
                          borderColor: '#e0e0e0',
                          color: COLORS.secondary
                        }}>
                          <i className="bi bi-person"></i>
                        </span>
                        <select
                          className="form-select"
                          name="id_agent"
                          value={filters.id_agent}
                          onChange={handleFilterChange}
                          style={{
                            borderColor: '#e0e0e0',
                            color: COLORS.darkText
                          }}
                          disabled={loadingFilters}
                        >
                          <option value="">Tous les agents</option>
                          {filterAgents.length > 0 ? (
                            filterAgents.map(agent => (
                              <option key={agent.id_agent || agent.id} value={agent.id_agent || agent.id}>
                                {agent.prenom && agent.nom 
                                  ? `${agent.prenom} ${agent.nom}`
                                  : agent.nom_agent || `Agent ${agent.id_agent || agent.id}`} 
                                
                              </option>
                            ))
                          ) : (
                            <option value="" disabled>Aucun agent disponible</option>
                          )}
                        </select>
                      </div>
                      <div className="form-text" style={{ fontSize: '0.75rem' }}>
                        {filterAgents.length} agent(s) disponible(s)
                      </div>
                    </div>
                  </div>
                  
                  <div className="d-flex justify-content-end gap-2 mt-3">
                    <button
                      className="btn fw-semibold"
                      onClick={resetFilters}
                      disabled={loadingFilters}
                      style={{
                        color: COLORS.primary,
                        borderColor: COLORS.primary,
                        backgroundColor: 'transparent',
                        borderRadius: '8px',
                        padding: '8px 20px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (!loadingFilters) {
                          e.target.style.backgroundColor = '#E3F2FD';
                          e.target.style.borderColor = COLORS.secondary;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!loadingFilters) {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.borderColor = COLORS.primary;
                        }
                      }}
                    >
                      <i className="bi bi-arrow-clockwise me-1"></i>
                      Réinitialiser
                    </button>
                    <button
                      className="btn fw-semibold"
                      onClick={applyFilters}
                      disabled={loadingFilters}
                      style={{
                        color: COLORS.white,
                        backgroundColor: COLORS.primary,
                        borderColor: COLORS.primary,
                        borderRadius: '8px',
                        padding: '8px 20px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (!loadingFilters) {
                          e.target.style.backgroundColor = COLORS.secondary;
                          e.target.style.borderColor = COLORS.secondary;
                          e.target.style.transform = 'translateY(-2px)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!loadingFilters) {
                          e.target.style.backgroundColor = COLORS.primary;
                          e.target.style.borderColor = COLORS.primary;
                          e.target.style.transform = 'translateY(0)';
                        }
                      }}
                    >
                      <i className="bi bi-check-lg me-1"></i>
                      Appliquer
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Statistiques */}
            <div className="row mb-4">
              <div className="col-md-4 mb-3">
                <div className="card" style={{ 
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  border: 'none',
                  borderLeft: `4px solid ${COLORS.primary}`
                }}>
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                        width: '50px',
                        height: '50px',
                        backgroundColor: '#E3F2FD',
                        color: COLORS.primary
                      }}>
                        <i className="bi bi-building fs-4"></i>
                      </div>
                      <div>
                        <div className="small" style={{ color: COLORS.primary, fontWeight: '600', textTransform: 'uppercase' }}>
                          Sites affectés
                        </div>
                        <h4 className="mt-1 mb-0" style={{ color: COLORS.darkText, fontWeight: '700' }}>
                          {statistiques.total_sites}
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-md-4 mb-3">
                <div className="card" style={{ 
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  border: 'none',
                  borderLeft: `4px solid ${COLORS.success}`
                }}>
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                        width: '50px',
                        height: '50px',
                        backgroundColor: '#e8f5e9',
                        color: COLORS.success
                      }}>
                        <i className="bi bi-person fs-4"></i>
                      </div>
                      <div>
                        <div className="small" style={{ color: COLORS.success, fontWeight: '600', textTransform: 'uppercase' }}>
                          Agents affectés
                        </div>
                        <h4 className="mt-1 mb-0" style={{ color: COLORS.darkText, fontWeight: '700' }}>
                          {statistiques.total_agents}
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-md-4 mb-3">
                <div className="card" style={{ 
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  border: 'none',
                  borderLeft: `4px solid ${COLORS.warning}`
                }}>
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                        width: '50px',
                        height: '50px',
                        backgroundColor: '#fff3e0',
                        color: COLORS.warning
                      }}>
                        <i className="bi bi-link fs-4"></i>
                      </div>
                      <div>
                        <div className="small" style={{ color: COLORS.warning, fontWeight: '600', textTransform: 'uppercase' }}>
                          Affectations totales
                        </div>
                        <h4 className="mt-1 mb-0" style={{ color: COLORS.darkText, fontWeight: '700' }}>
                          {statistiques.total_affectations}
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Liste des affectations */}
            {affectations.length === 0 ? (
              <div className="text-center py-5">
                <div className="mb-4">
                  <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                    width: '80px',
                    height: '80px',
                    backgroundColor: '#F5F7FA',
                    color: '#B0BEC5'
                  }}>
                    <i className="bi bi-people fs-1"></i>
                  </div>
                </div>
                <h6 className="mb-3" style={{ color: COLORS.primary }}>Aucune affectation trouvée</h6>
                <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                  {filters.id_site || filters.id_agent 
                    ? "Aucune affectation ne correspond à vos critères de recherche."
                    : "Aucune affectation agent-site n'a été créée pour le moment."}
                </p>
                {isGestionnaire && (
                  <button
                    className="btn fw-semibold mt-3"
                    onClick={() => navigate("/affectations/create")}
                    style={{
                      color: COLORS.white,
                      backgroundColor: COLORS.primary,
                      borderColor: COLORS.primary,
                      borderRadius: '8px',
                      padding: '10px 24px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = COLORS.secondary;
                      e.target.style.borderColor = COLORS.secondary;
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = COLORS.primary;
                      e.target.style.borderColor = COLORS.primary;
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    <i className="bi bi-plus-lg me-1"></i>
                    Créer la première affectation
                  </button>
                )}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr style={{ 
                      backgroundColor: COLORS.lightBg,
                      borderBottom: `2px solid ${COLORS.accent}`
                    }}>
                      <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Site</th>
                      <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Agent</th>
                      <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Date affectation</th>
                      <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Statuts</th>
                      <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {affectations.map((affectation) => (
                      <tr 
                        key={`${affectation.id_site}-${affectation.id_agent}`}
                        style={{ borderBottom: `1px solid ${COLORS.lightBg}` }}
                        className={isDeleted(affectation) ? "table-warning" : ""}
                      >
                        <td style={{ padding: '16px' }}>
                          <div className="d-flex align-items-center">
                            <div className="me-3">
                              <div 
                                className="text-white rounded-circle d-flex align-items-center justify-content-center fw-semibold"
                                style={{ 
                                  width: '44px', 
                                  height: '44px', 
                                  fontSize: '15px',
                                  backgroundColor: COLORS.primary,
                                  boxShadow: '0 2px 4px rgba(11, 60, 93, 0.1)'
                                }}
                              >
                                <i className="bi bi-building"></i>
                              </div>
                            </div>
                            <div>
                              <Link 
                                to={`/sites/details/${affectation.id_site}`}
                                className="text-decoration-none fw-semibold"
                                style={{ color: COLORS.primary }}
                              >
                                {formatSiteName(affectation.site)}
                              </Link>
                              <div className="small" style={{ color: '#6c757d', fontSize: '0.8rem' }}>
                                {affectation.site?.adresse || affectation.site?.addresse_site || "Pas d'adresse"}
                                <span className="badge fw-semibold ms-2" style={{ 
                                  backgroundColor: '#E3F2FD',
                                  color: COLORS.secondary,
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontSize: '0.7rem'
                                }}>
                                 
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                        
                        <td style={{ padding: '16px' }}>
                          <div className="d-flex align-items-center">
                            <div className="me-3">
                              <div 
                                className="text-white rounded-circle d-flex align-items-center justify-content-center fw-semibold"
                                style={{ 
                                  width: '44px', 
                                  height: '44px', 
                                  fontSize: '15px',
                                  backgroundColor: '#4caf50',
                                  boxShadow: '0 2px 4px rgba(76, 175, 80, 0.1)'
                                }}
                              >
                                <i className="bi bi-person"></i>
                              </div>
                            </div>
                            <div>
                              <Link 
                                to={`/agents/details/${affectation.id_agent}`}
                                className="text-decoration-none fw-semibold"
                                style={{ color: COLORS.darkText }}
                              >
                                {formatAgentName(affectation.agent)}
                              </Link>
                              <div className="small" style={{ color: '#6c757d', fontSize: '0.8rem' }}>
                                Matricule: {affectation.agent?.matricule || "N/A"}
                                <span className="badge fw-semibold ms-2" style={{ 
                                  backgroundColor: '#E3F2FD',
                                  color: COLORS.secondary,
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontSize: '0.7rem'
                                }}>
                                  
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                        
                        <td style={{ padding: '16px' }}>
                          <div>
                            <div style={{ color: COLORS.darkText, fontWeight: '500', fontSize: '0.9rem' }}>
                              {formatDate(affectation.date_affectation)}
                            </div>
                            <div className="small" style={{ color: '#6c757d', fontSize: '0.8rem' }}>
                              <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-1" style={{ 
                                width: '16px',
                                height: '16px',
                                backgroundColor: '#E3F2FD',
                                color: COLORS.secondary,
                                fontSize: '0.6rem'
                              }}>
                                <i className="bi bi-calendar"></i>
                              </div>
                              Date d'affectation
                            </div>
                          </div>
                        </td>
                        
                        <td style={{ padding: '16px' }}>
                          <div className="d-flex flex-wrap gap-2">
                            <span className="badge fw-semibold" style={{ 
                              backgroundColor: (affectation.site?.statut || affectation.site?.statut_site || 0) === 1 ? '#e8f5e9' : '#f5f5f5',
                              color: (affectation.site?.statut || affectation.site?.statut_site || 0) === 1 ? '#2e7d32' : '#757575',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              fontSize: '0.8rem'
                            }}>
                              <i className={`bi bi-building me-1`}></i>
                              Site {(affectation.site?.statut || affectation.site?.statut_site || 0) === 1 ? 'Actif' : 'Inactif'}
                            </span>
                            <span className="badge fw-semibold" style={{ 
                              backgroundColor: (affectation.agent?.statut || affectation.agent?.statut_agent || 0) === 1 ? '#e8f5e9' : '#f5f5f5',
                              color: (affectation.agent?.statut || affectation.agent?.statut_agent || 0) === 1 ? '#2e7d32' : '#757575',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              fontSize: '0.8rem'
                            }}>
                              <i className={`bi bi-person me-1`}></i>
                              Agent {(affectation.agent?.statut || affectation.agent?.statut_agent || 0) === 1 ? 'Actif' : 'Inactif'}
                            </span>
                            <span className="badge fw-semibold" style={{ 
                              backgroundColor: (affectation.agent?.affecte || 0) === 1 ? '#E3F2FD' : '#fff3e0',
                              color: (affectation.agent?.affecte || 0) === 1 ? COLORS.secondary : '#ff9800',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              fontSize: '0.8rem'
                            }}>
                              <i className={`bi bi-link me-1`}></i>
                              {(affectation.agent?.affecte || 0) === 1 ? 'Affecté' : 'Non affecté'}
                            </span>
                            {affectation.periode && (
                              <span className="badge fw-semibold" style={{
                                backgroundColor: affectation.periode === 'jour' ? '#fff8e1' : '#e8eaf6',
                                color: affectation.periode === 'jour' ? '#ff8f00' : '#3949ab',
                                padding: '6px 12px',
                                borderRadius: '6px',
                                fontSize: '0.8rem'
                              }}>
                                <i className={`bi ${affectation.periode === 'jour' ? 'bi-sun' : 'bi-moon-stars'} me-1`}></i>
                                {affectation.periode === 'jour' ? 'Journée' : 'Soirée'}
                              </span>
                            )}
                          </div>
                        </td>
                        
                        <td style={{ padding: '16px' }}>
                          {isGestionnaire && (
                            <button
                              className="btn fw-semibold"
                              onClick={() => handleDeleteAffectation(affectation)}
                              disabled={actionLoading}
                              title="Retirer l'affectation"
                              style={{
                                color: hoveredDelete === `${affectation.id_site}-${affectation.id_agent}` ? COLORS.white : COLORS.danger,
                                borderColor: COLORS.danger,
                                backgroundColor: hoveredDelete === `${affectation.id_site}-${affectation.id_agent}` ? COLORS.danger : 'transparent',
                                borderRadius: '6px',
                                padding: '6px 12px',
                                fontSize: '0.85rem',
                                transition: 'all 0.3s ease'
                              }}
                              onMouseEnter={() => setHoveredDelete(`${affectation.id_site}-${affectation.id_agent}`)}
                              onMouseLeave={() => setHoveredDelete(null)}
                            >
                              <i className="bi bi-person-dash"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Modal de confirmation de suppression */}
      {showDeleteModal && selectedAffectation && (
        <>
          <div className="modal-backdrop fade show"></div>
          
          <div 
            className="modal fade show d-block" 
            tabIndex="-1"
            style={{ display: 'block' }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content" style={{ 
                borderRadius: '12px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                border: 'none'
              }}>
                <div className="modal-header" style={{ 
                  backgroundColor: '#ffebee',
                  borderBottom: `2px solid ${COLORS.danger}`,
                  borderRadius: '12px 12px 0 0'
                }}>
                  <h5 className="modal-title" style={{ color: '#c62828', fontWeight: '600' }}>
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    Confirmer le retrait
                  </h5>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setShowDeleteModal(false)}
                    disabled={actionLoading}
                  ></button>
                </div>
                <div className="modal-body">
                  <p style={{ color: COLORS.darkText }}>
                    Êtes-vous sûr de vouloir retirer l'affectation entre :
                  </p>
                  <div className="alert alert-warning" style={{ 
                    backgroundColor: '#fff3e0',
                    borderColor: '#ff9800',
                    color: '#ff6f00',
                    borderRadius: '8px'
                  }}>
                    <div className="d-flex align-items-center mb-3">
                      <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                        width: '32px',
                        height: '32px',
                        backgroundColor: '#E3F2FD',
                        color: COLORS.primary
                      }}>
                        <i className="bi bi-building"></i>
                      </div>
                      <div>
                        <strong>Site :</strong> {formatSiteName(selectedAffectation.site)}
                      </div>
                    </div>
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                        width: '32px',
                        height: '32px',
                        backgroundColor: '#e8f5e9',
                        color: '#2e7d32'
                      }}>
                        <i className="bi bi-person"></i>
                      </div>
                      <div>
                        <strong>Agent :</strong> {formatAgentName(selectedAffectation.agent)}
                      </div>
                    </div>
                  </div>
                  <p className="text-muted small" style={{ color: '#6c757d' }}>
                    <i className="bi bi-info-circle me-1"></i>
                    Cette action est irréversible. L'agent sera marqué comme "non affecté".
                  </p>
                </div>
                <div className="modal-footer" style={{ borderTop: `1px solid ${COLORS.lightBg}` }}>
                  <button
                    className="btn fw-semibold"
                    onClick={() => setShowDeleteModal(false)}
                    disabled={actionLoading}
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '10px 20px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!actionLoading) {
                        e.target.style.backgroundColor = '#E3F2FD';
                        e.target.style.borderColor = COLORS.secondary;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!actionLoading) {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.borderColor = COLORS.primary;
                      }
                    }}
                  >
                    Annuler
                  </button>
                  <button
                    className="btn fw-semibold"
                    onClick={confirmDelete}
                    disabled={actionLoading}
                    style={{
                      color: COLORS.white,
                      backgroundColor: COLORS.danger,
                      borderColor: COLORS.danger,
                      borderRadius: '8px',
                      padding: '10px 24px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!actionLoading) {
                        e.target.style.backgroundColor = '#d32f2f';
                        e.target.style.borderColor = '#d32f2f';
                        e.target.style.transform = 'translateY(-2px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!actionLoading) {
                        e.target.style.backgroundColor = COLORS.danger;
                        e.target.style.borderColor = COLORS.danger;
                        e.target.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    {actionLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Retrait...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-person-dash me-2"></i>
                        Retirer l'affectation
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Styles CSS */}
      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .modal-title,
        .alert strong,
        .table th {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .modal-body,
        .form-control,
        .form-select,
        .form-text,
        .alert,
        .table td,
        small {
          font-family: 'Roboto', sans-serif;
        }
        
        .modal-backdrop {
          z-index: 1040;
          background-color: rgba(0, 0, 0, 0.5);
        }
        
        .modal {
          z-index: 1050;
        }
        
        .table-hover tbody tr:hover {
          background-color: ${COLORS.lightBg};
          transition: background-color 0.2s ease;
        }
        
        .btn:focus,
        .form-control:focus,
        .page-link:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn,
        .badge {
          transition: all 0.2s ease-in-out;
        }
        
        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .form-control:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
          
          .table-responsive {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          
          .table th,
          .table td {
            padding: 12px 8px !important;
          }
          
          .modal-dialog {
            margin: 16px;
          }
          
          .col-md-4 {
            margin-bottom: 16px;
          }
        }
        
        /* Amélioration de la lisibilité des tables */
        .table th {
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-size: 0.85rem;
        }
        
        .table td {
          vertical-align: middle;
        }
        
        /* Animation pour les badges */
        .badge:hover {
          transform: translateY(-1px);
        }
      `}</style>
    </Layout>
  );
};

export default AffectationsList;